<template>
    <kiosk-layout>
        <template v-if="!loading" v-slot:subtitle>
            <a href="/kiosk/groups" class="no-underline! text-sky-600! text-base font-normal">
                <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                Search
            </a>
        </template>
        <template v-if="!loading" v-slot:title>
            <span class="inline-flex items-center">
                {{ group.name ?? group.group_id }}
                <group-status-badge v-if="group.status" :status="group.status" class="ms-3" />
            </span>
        </template>
        <template v-if="!loading" v-slot:actions>
            <vx-button
                color="primary"
                :href="
                    team
                        ? `/kiosk/users/${team.owner_id}/impersonate?team_id=${props.teamId}&group_id=${props.groupId}`
                        : '#'
                "
                :disabled="!team || team.owner_id === currentUserId || team.status === 'closed'"
            >
                Impersonate Owner <font-awesome-icon widthAuto :icon="faMasksTheater"></font-awesome-icon>
            </vx-button>
        </template>
        <template v-if="!loading && !contactGroupsDisabled" v-slot:sidebar>
            <page-sidebar :links="navigationLinks"></page-sidebar>
        </template>

        <page-loading v-if="loading"></page-loading>
        <vx-alert
            v-else-if="contactGroupsDisabled"
            data-test="contact-groups-disabled"
            color="warning"
            :closeable="false"
            shadow
        >
            Contact groups are disabled for this team. Enable the feature to manage this group.
        </vx-alert>
        <slot v-else></slot>
    </kiosk-layout>
</template>

<script setup>
import { faArrowLeft, faMasksTheater } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { onMounted, computed } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import { VxAlert, VxButton } from '@voxie/frontend-components';
import { useGroupDetails } from '../composables/useGroupDetails';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import GroupStatusBadge from '~/components/general/GroupStatusBadge.vue';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    groupId: {
        type: String,
        required: true,
    },
});

const { mount, group, loading, team, contactGroupsDisabled } = useGroupDetails();

const currentUserId = Spark.state.user.id;

const navigationLinks = computed(() => {
    const links = [
        { href: `/kiosk/teams/${props.teamId}/groups/${props.groupId}`, label: 'Details' },
        { href: `/kiosk/teams/${props.teamId}/groups/${props.groupId}/users`, label: 'Users' },
        { href: `/kiosk/teams/${props.teamId}/groups/${props.groupId}/configuration`, label: 'Configuration' },
    ];

    if (!team.value && loading.value) {
        return links;
    }

    links.push({
        href: `/kiosk/teams/${props.teamId}/groups/${props.groupId}/status-log`,
        label: 'Status Log',
    });

    links.push({
        href: `/kiosk/teams/${props.teamId}/groups/${props.groupId}/billing-config`,
        label: 'Billing Config',
    });

    if (group.value?.is_billable) {
        links.push({
            href: `/kiosk/teams/${props.teamId}/groups/${props.groupId}/billing-details`,
            label: 'Billing Details',
        });

        if (team.value?.status !== 'closed') {
            links.push({
                href: `/kiosk/teams/${props.teamId}/groups/${props.groupId}/proforma-invoices`,
                label: 'Upcoming Invoices',
            });
        }

        links.push({
            href: `/kiosk/teams/${props.teamId}/groups/${props.groupId}/historical-invoices`,
            label: 'Historical Invoices',
        });
    }

    return links;
});

onMounted(() => {
    mount(props.teamId, props.groupId);
});
</script>
